import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowLeft, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/site-data";

const DURATION = 6500;

export const Hero = () => {
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.35]);

  const next = useCallback(() => setIdx((i) => (i + 1) % IMAGES.heroes.length), []);
  const prev = () => setIdx((i) => (i - 1 + IMAGES.heroes.length) % IMAGES.heroes.length);

  useEffect(() => {
    const t = setInterval(next, DURATION);
    return () => clearInterval(t);
  }, [next, idx]);

  const slide = IMAGES.heroes[idx];

  return (
    <section id="top" data-testid="hero" ref={ref} className="relative h-[calc(100vh-128px)] min-h-[560px] overflow-hidden bg-[#14171a] hero-grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        {IMAGES.heroes.map((s, i) => (
          <motion.img
            key={s.src}
            src={s.src}
            alt={s.kicker}
            data-testid={i === idx ? `hero-slide-${idx}` : undefined}
            initial={false}
            animate={{ opacity: i === idx ? 1 : 0, scale: i === idx ? 1 : 1.07 }}
            transition={{ opacity: { duration: 1.1, ease: "easeInOut" }, scale: { duration: 6.5, ease: "linear" } }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-[#14171a]/90 via-[#14171a]/45 to-[#14171a]/15" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#14171a]/85 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-center">
        <AnimatePresence mode="wait">
          <motion.div key={idx} initial="hidden" animate="show" exit="exit" className="max-w-3xl">
            <motion.p
              variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } }, exit: { opacity: 0, y: -10, transition: { duration: 0.25 } } }}
              data-testid="hero-kicker"
              className="font-display text-[#e2001a] font-semibold tracking-[0.28em] uppercase text-xs sm:text-sm mb-6 flex items-center gap-3"
            >
              <span className="inline-block w-10 h-[2px] bg-[#e2001a]" />
              {slide.kicker}
            </motion.p>
            <h1 data-testid="hero-title" className="font-display font-bold text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              {slide.title.map((line, i) => (
                <span className="mask-line" key={line}>
                  <motion.span
                    variants={{
                      hidden: { y: "110%" },
                      show: { y: 0, transition: { duration: 0.7, delay: 0.12 + i * 0.1, ease: [0.22, 1, 0.36, 1] } },
                      exit: { y: "-110%", transition: { duration: 0.3 } },
                    }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.5 } }, exit: { opacity: 0, transition: { duration: 0.2 } } }}
              data-testid="hero-text"
              className="mt-7 text-white/70 text-base sm:text-lg max-w-xl leading-relaxed"
            >
              {slide.text}
            </motion.p>
            <motion.div
              variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.65 } }, exit: { opacity: 0, transition: { duration: 0.2 } } }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                data-testid="hero-cta-primary"
                href="#kontakt"
                className="inline-flex items-center gap-2 bg-[#e2001a] hover:bg-[#b80016] text-white font-display font-semibold px-8 py-4 rounded-full transition-colors duration-300 group"
              >
                Angebot anfordern
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                data-testid="hero-cta-secondary"
                href="#referenzen"
                className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white font-display font-medium px-8 py-4 rounded-full transition-colors duration-300"
              >
                Referenzen ansehen
              </a>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      <div className="absolute z-20 bottom-8 inset-x-0">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
          <div data-testid="hero-counter" className="font-display text-white/60 text-sm tracking-widest">
            <span className="text-white text-xl font-semibold">0{idx + 1}</span> / 0{IMAGES.heroes.length}
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="hero-prev" onClick={prev} aria-label="Zurück" className="w-11 h-11 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-white hover:text-[#14171a] transition-colors duration-300">
              <ArrowLeft size={17} />
            </button>
            <button data-testid="hero-next" onClick={next} aria-label="Weiter" className="w-11 h-11 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-white hover:text-[#14171a] transition-colors duration-300">
              <ArrowRight size={17} />
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-5">
          <div className="h-[2px] bg-white/15 w-full overflow-hidden">
            <motion.div
              key={idx}
              data-testid="hero-progress"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: DURATION / 1000, ease: "linear" }}
              className="h-full bg-[#e2001a]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
